/**
 * The idle world on the isometric map (D240: the map is the reward). Pure: the world state
 * and a time in, parcels and buildings out, for `IsoWorldMap`'s still mode.
 *
 * Each producer has a plot of land kept for it. Owning more fills more of it: a field starts
 * as a block of `FIRST_PLOT` tiles and gains a tile per field bought, one hen per hen. A
 * field's colour follows its bar, so the crop ripens as the bar fills and is cut when it pays.
 *
 * D259: the world drawn is only the land in use, so a fresh world is the yard and its wheat,
 * framed large, and the world grows outward as producers are unlocked.
 *
 * D348: the grid logic here is every theme's; what each producer's plot is, where each chain's
 * building stands and the country round the world come from the world's theme's map kit
 * (`mapKitFor` in `ui/iso-kit.ts`; the world's own is `ui/iso-kit-world.ts`).
 *
 * Adding a producer to the map (the one place this is written down):
 *  1. Give it a slot in its theme's kit (`SLOTS` in `ui/iso-kit-world.ts` for the world): where
 *     its land starts, its size and what is drawn on it; a chain's building goes in the kit's
 *     `chainBuildings`. The world kit's own notes say what to keep clear of.
 *  2. Give it an icon: draw `packages/art/producers/{id}.svg` (24 box, 2px stroke,
 *     currentColor, accent parts `class="a"`, the accent token as `data-accent` on the
 *     root). `npm run art` builds it into the sprite as `producer_{id}`, and `iconFor` in
 *     `idle/view.ts` picks it up with no other change.
 */
import { getIdleContent } from '@hedgerow/data';
import {
  idleDecorations,
  idleLayout,
  idleMarkTile,
  idleOpenLands,
  idleSeason,
  idleTrackFor,
  idleTrackPlan,
  type IdleTrackEdge,
  type IdleWorldState,
} from '@hedgerow/sim';
import { DENSITY, MEMORIAL } from './appearance.ts';
import { styleOf } from '../ui/iso-paint.ts';
import type { IsoBuildingAt } from '../ui/IsoWorldMap.tsx';
import {
  fieldCycleStage,
  heartForm,
  snowAmount,
  plotGaps,
  type Bounds,
  type TileKey,
  type WorldBounds,
  type IsoParcel,
  type Stage,
} from '../ui/iso.ts';
import { fenceOnBoundary, mapKitFor, themeOf, type MapKit, type PlotSlot } from '../ui/iso-kit.ts';

/* D348: the world's own plots, chain buildings and country, kept reachable from here. */
export { CHAIN_BUILDINGS, SLOTS, landscapeFor } from '../ui/iso-kit-farm.ts';

type Slot = PlotSlot;

/** The yard the farmhouse and barn stand on. Every world has it. */
const YARD: Slot = { x: 0, y: 0, w: 6, h: 3, kind: 'yard' };

/** D259: a field's first purchase is drawn as a block this big, so it reads at phone size. */
export const FIRST_PLOT = 9;

/** D259: festival marks stand in rows of this many along the back, east of the barn. */
export const MARKS_PER_ROW = 4;

/**
 * Where the `i`th festival's mark stands (D245, D259): two tiles square, in a row beside
 * the barn along the world's back edge, oldest nearest the barn. A full row is followed by
 * the next one behind it, so the world shows its years.
 */
export function markTile(i: number): { x: number; y: number; w: number; h: number } {
  const row = Math.floor(i / MARKS_PER_ROW);
  return { x: YARD.x + YARD.w + (i % MARKS_PER_ROW) * 2, y: YARD.y - 2 * row, w: 2, h: 2 };
}

/** At most this many hives stand on the meadow. */
export const MAX_HIVES = 12;

/** At most this many hens or cows stand on the land; past it the land is simply full. */
export const MAX_ANIMALS = 14;

/** A bar this short is a blur, so its field is drawn ripe rather than flickering. */
const BLUR_MS = 2000;

/** The slot's tiles in the order they fill: front row first, left to right. */
export function slotTiles(s: Pick<Slot, 'x' | 'y' | 'w' | 'h'>, n: number): string[] {
  const out: string[] = [];
  for (let y = s.y; y < s.y + s.h && out.length < n; y++)
    for (let x = s.x; x < s.x + s.w && out.length < n; x++) out.push(`${x},${y}`);
  return out;
}

/**
 * A field's tiles in the order they fill (D259): a square growing out from the slot's back
 * corner, so the first few purchases are a block rather than a strip.
 */
export function blockTiles(s: Pick<Slot, 'x' | 'y' | 'w' | 'h'>, n: number): string[] {
  const all: Array<[number, number]> = [];
  for (let dy = 0; dy < s.h; dy++) for (let dx = 0; dx < s.w; dx++) all.push([dx, dy]);
  all.sort((a, b) => Math.max(...a) - Math.max(...b) || a[1] - b[1] || a[0] - b[0]);
  return all.slice(0, Math.max(0, n)).map(([dx, dy]) => `${s.x + dx},${s.y + dy}`);
}

/** How ripe a field is at `t`: 0 not started, 1 to 5 as its bar fills. */
export function fieldStage(
  ps: { hand: boolean; running: boolean; progressMs: number; cycleMs: number },
  advancedTo: number,
  t: number,
): Stage {
  if (!ps.running) return 0;
  if (ps.cycleMs < BLUR_MS) return 4;
  const start = advancedTo - ps.progressMs;
  let into = t - start;
  if (ps.hand) into = ((into % ps.cycleMs) + ps.cycleMs) % ps.cycleMs;
  const fill = Math.max(0, Math.min(1, into / ps.cycleMs));
  return Math.min(5, 1 + Math.floor(fill * 5)) as Stage;
}

/**
 * Where everything on the world stands (D267): read from the world's own seeded layout, or
 * the default plan for a world made before it, which is exactly the placement above, so an
 * old save keeps its look. Every other part of the scene reads placement from here.
 */
export interface WorldLayout {
  yard: Bounds;
  /** The farmhouse, at the yard's end nearest the world's west edge, or east when mirrored. */
  house: { x: number; y: number };
  /** Which way the barn grows from the house: +1 east, -1 west when mirrored. */
  barnDir: 1 | -1;
  slots: Record<string, Slot>;
  chains: Record<string, IsoBuildingAt>;
  markAt: (i: number) => Bounds;
  /** Trees and the stream, in the margin round the world. */
  trees: Array<{ x: number; y: number }>;
  stream: Array<{ x: number; y: number }>;
  /** D287: the river, which every world has: its stream, or one laid from its seed. */
  river: Array<{ x: number; y: number }>;
  /** D301: each piece of land beside the world, where it stands and whether it is open yet. */
  lands: Record<string, { bounds: Bounds; open: boolean }>;
  /** D329: the world's seed, and the world gate the lane leaves by. */
  seed: number;
  gate: { x: number; y: number };
  /** D329: the world's full width and depth, for which way the lane runs from the gate. */
  width: number;
  height: number;
  /** D329: the lane from the barn door out to the gate, in tile corners. */
  lane: Array<{ x: number; y: number }>;
  /** D344: ground within the world's width and depth that is outside its fence. */
  cuts: Bounds[];
  /** D344: the world's pond, when it has one. */
  pond?: Bounds;
  /** D344: how the world's kind shapes the country round it. */
  scenery: Scenery;
  /** D440: the lone tiles the seed leaves between fields or plots, each for a filler. */
  lone: TileKey[];
}

/** D344: a kind's bias on the country: hills, copses, grazing, the village's distance and size. */
export type Scenery = ReturnType<typeof getIdleContent>['map']['kinds'][number]['scenery'];

export function worldLayout(state: IdleWorldState, stored?: string): WorldLayout {
  // D348: the world's own theme's content: its producers' places, its kinds and its lands.
  const content = getIdleContent(themeOf(state, stored));
  // D348: the theme's own plots and chain buildings; D350: its own placement, if it asks.
  const kit = mapKitFor(themeOf(state, stored));
  const seeded = kit.layout?.seeded ?? true;
  const lay = idleLayout(state, content);
  const m = lay.mirrored;
  const openLands = new Set(idleOpenLands(state, content));
  /* A building on a plot keeps its place within the plot, mirrored with it (D267). Land
     producers (D301) take their plot from the piece of land they stand on instead, already
     placed outside the farm by the seeded layout; no mirroring of its own. */
  const slots: Record<string, Slot> = {};
  for (const [id, base] of Object.entries(kit.plots)) {
    const r = !seeded
      ? base
      : base.land
        ? (lay.lands?.[base.land]?.plots[id] ?? base)
        : (lay.plots[id] ?? base);
    const slot: Slot = { ...base, x: r.x, y: r.y, w: r.w, h: r.h };
    if (base.house) {
      let rx = base.house.x - base.x;
      const ry = base.house.y - base.y;
      // D344: a kind's plot may be another size, so the house keeps to the edge it stood by.
      const fromRight = base.w - rx - base.house.w;
      if (lay.kind && fromRight < rx) rx = r.w - fromRight - base.house.w;
      slot.house = {
        ...base.house,
        x: r.x + (m ? r.w - rx - base.house.w : rx),
        y: r.y + ry,
      };
    }
    slots[id] = slot;
  }
  const chains: Record<string, IsoBuildingAt> = {};
  // D440: one rule on every layout, the default plan's and the classic ones too: each chain's
  // building on its own producer's plot.
  Object.assign(chains, chainsOnPlots(slots, kit, content.chains));
  const lands: Record<string, { bounds: Bounds; open: boolean }> = {};
  for (const l of content.map.lands) {
    const b = lay.lands?.[l.id];
    if (b) lands[l.id] = { bounds: { x: b.x, y: b.y, w: b.w, h: b.h }, open: openLands.has(l.id) };
  }
  const yard = lay.yard;
  const plan = idleTrackPlan(lay);
  return {
    yard,
    house: { x: m ? yard.x + yard.w - 2 : yard.x, y: yard.y },
    barnDir: m ? -1 : 1,
    slots,
    chains,
    markAt: (i) => idleMarkTile(lay, i),
    trees: lay.trees,
    stream: lay.stream,
    river: lay.river ?? lay.stream,
    lands,
    seed: lay.seed,
    gate: plan.gate,
    width: lay.width,
    height: lay.height,
    lane: plan.lane,
    cuts: lay.cuts ?? [],
    ...(lay.pond ? { pond: lay.pond } : {}),
    scenery: content.map.kinds.find((k) => k.id === lay.kind)?.scenery ?? PLAIN_SCENERY,
    // D440: the lone tiles the seeded layout left on purpose, by chance from the seed.
    lone: [...(lay.fillers ?? [])],
  };
}

/** D344: the country round a world made before kinds, as D329 drew it. */
const PLAIN_SCENERY: Scenery = { hills: 1, copse: 1, pasture: 1, village: 1, houses: 0 };

/**
 * D344: each chain's building on a kind's world, on its own producer's plot: in the corner its
 * crop fills last, turned if it fits better, clear of the plot's house and of the plot's other
 * building. Its tiles go on the plot's `hold`, so the crop never grows under it, built or not.
 * A building that finds no room is left off.
 */
function chainsOnPlots(
  slots: Record<string, Slot>,
  kit: MapKit,
  chainsOf: ReturnType<typeof getIdleContent>['chains'],
): Record<string, IsoBuildingAt> {
  const out: Record<string, IsoBuildingAt> = {};
  for (const s of Object.values(slots)) s.hold = [];
  for (const [id, b] of Object.entries(kit.chainBuildings)) {
    const s = slots[chainsOf.find((c) => c.id === id)?.producer ?? ''];
    if (!s) continue;
    const order =
      s.kind === 'field' || s.crocus ? blockTiles(s, s.w * s.h) : slotTiles(s, s.w * s.h);
    const rank = new Map(order.map((k, i) => [k, i]));
    const busy = new Set(s.hold ?? []);
    if (s.house) for (const k of slotTiles(s.house, s.house.w * s.house.h)) busy.add(k);
    let best: { at: IsoBuildingAt; tiles: string[]; score: number } | null = null;
    for (const [w, h] of b.w === b.h
      ? [[b.w, b.h]]
      : [
          [b.w, b.h],
          [b.h, b.w],
        ])
      for (let y = s.y; y + h! <= s.y + s.h; y++)
        for (let x = s.x; x + w! <= s.x + s.w; x++) {
          const tiles = slotTiles({ x, y, w: w!, h: h! }, w! * h!);
          if (tiles.some((k) => busy.has(k))) continue;
          const ranks = tiles.map((k) => rank.get(k) ?? 0);
          // The latest the crop would reach any of its tiles, then all of them together.
          const score = Math.min(...ranks) * 1e4 + ranks.reduce((a, r) => a + r, 0);
          if (!best || score > best.score)
            best = { at: { ...b, x, y, w: w!, h: h! }, tiles, score };
        }
    if (!best) continue;
    out[id] = best.at;
    s.hold = [...(s.hold ?? []), ...best.tiles];
  }
  return out;
}

/** The tiles a plot fills in order: the field's block or the pen's rows, its `hold` left out. */
function fillOrder(s: Slot, block: boolean): string[] {
  const all = block ? blockTiles(s, s.w * s.h) : slotTiles(s, s.w * s.h);
  const held = new Set(s.hold);
  return all.filter((k) => !held.has(k));
}

/**
 * D268: how far a producer has come: the milestones reached at `owned`, and the last of
 * them as its tier sign says it (`25`), from the producer's own milestones in the data.
 */
export function tierOf(id: string, owned: number, theme?: string): { tier: number; sign?: string } {
  // D348: the producer in its own theme's content; the world's by default.
  const p = getIdleContent(theme).producers.find((q) => q.id === id);
  const reached = (p?.milestones ?? []).filter((m) => m.owned <= owned);
  const last = reached[reached.length - 1];
  return last ? { tier: reached.length, sign: String(last.owned) } : { tier: 0 };
}

/** D268: the tier of every producer on the world, so a screen can tell when one goes up. */
export function worldTiers(state: IdleWorldState, stored?: string): Record<string, number> {
  const out: Record<string, number> = {};
  const theme = themeOf(state, stored);
  for (const [id, ps] of Object.entries(state.year.producers))
    out[id] = tierOf(id, ps.owned, theme).tier;
  return out;
}

export interface IdleScene {
  /**
   * D331: the hedge and gate decorations on show, for the flat map, and the world gate: where the
   * lane leaves the land (`x`, `y`) and the lane's step before it (`px`, `py`), in tile corners.
   */
  decor: {
    hedge: string;
    gate: string;
    /** D420: the fence decoration, in a theme with no pens, for its boundary. */
    fence?: string;
    gateAt?: { x: number; y: number; px: number; py: number };
    /** D437: the open track (D287), for the flat map's lanes. */
    track?: { surface: string; edges: IdleTrackEdge[] };
    /** D437: the gaps between neighbouring plots, paved from the heart's form 1. */
    paving?: TileKey[];
    /** D440: how far winter's snow has come, 0 to 1 (`worldSnow`). */
    snow?: number;
    /** D440: each lone tile's filler (`worldFillers`). */
    fillers?: Filler[];
  };
  parcels: IsoParcel[];
  buildings: IsoBuildingAt[];
  /** The camera's own frame: the world and every open piece of land beside it. */
  plot: Bounds;
  /** D301: the main world's own edge, for its hedge, apart from any open land beside it. D344:
   * with the ground cut from its outline. */
  worldPlot: WorldBounds;
  /** D301: each open piece of land beside the world, for its own hedge round all four sides. */
  lands: Bounds[];
  /** D267: the seeded trees round the world, as country wood, one tile each. D329: and the
   * neighbours' land beyond them (`landscapeFor`). */
  country: CountryItem[];
  /** D267: the seeded stream round the world, as a line through its tiles' middles. */
  stream: Array<[number, number]>;
  /** D348: the world's theme, which picks the renderers' kits. */
  theme: string;
}

export interface IdleSceneOptions {
  /**
   * D259: the festival (its index in `permanent.festivals`) whose mark has just gone down.
   * That mark is drawn with the map's arrival pulse. Pass the newest index after a festival.
   */
  pulseMark?: number;
  /** D268: the producer that has just reached a milestone. Its plot plays its moment. */
  burst?: string;
  /** D348: the world's stored theme, as the server sent it; it wins over anything inferred. */
  theme?: string;
}

/** The world as the map draws it at time `t`. */
export function idleScene(
  state: IdleWorldState,
  t: number,
  opts: IdleSceneOptions = {},
): IdleScene {
  const parcels: IsoParcel[] = [];
  const L = worldLayout(state, opts.theme);
  // D348: the world theme's own map kit.
  const theme = themeOf(state, opts.theme);
  const kit = mapKitFor(theme);
  const content = getIdleContent(theme);
  // D291: the decorations on show, carried on the buildings and parcels they restyle.
  const decor = idleDecorations(state, content);
  // D331: the roof decoration rides on the building's look after a `+` (`buildingDecor`).
  const roofDef = content.decorations.find((d) => d.kind === 'roof')?.id;
  const look = {
    look: decor.roof === roofDef ? decor.building : `${decor.building}+${decor.roof}`,
  };
  // The barn grows with its level, so a bigger barn is seen as well as read.
  const barnW = Math.min(4, 1 + state.permanent.barnLevel);
  // D430: the world's age, uncapped, on its home, for the country that grows with it.
  const age = {
    years: state.permanent.festivals.length,
    generations: state.permanent.lineage?.generation ?? 0,
  };
  // D422, D423: a theme whose home is a heart stands it on the whole yard, the store part of it.
  const buildings: IsoBuildingAt[] = kit.heart
    ? [
        {
          ...L.yard,
          kind: 'house',
          use: 'home',
          ...look,
          age,
          heart: {
            yard: L.yard,
            dir: L.barnDir,
            producers: Object.values(state.year.producers).filter((p) => p.owned > 0).length,
            years: state.permanent.festivals.length,
            generations: state.permanent.lineage?.generation ?? 0,
            store: Math.max(0, state.permanent.barnLevel - 1),
          },
        },
      ]
    : [
        { x: L.house.x, y: L.house.y, w: 2, h: 2, kind: 'house', use: 'home', ...look, age },
        {
          x: L.barnDir > 0 ? L.house.x + 2 : L.house.x - barnW,
          y: L.house.y,
          w: barnW,
          h: 2,
          kind: 'barn',
          use: 'storage',
          ...look,
        },
      ];
  // D259: the land drawn is the land in use, so the camera frames only that. D301: a piece of
  // land beside the world keeps its own hedge, so `world` (the main world's own edge, for its
  // hedge) is tracked apart from the camera, which frames both.
  let x0 = L.yard.x,
    y0 = L.yard.y,
    x1 = L.yard.x + L.yard.w,
    y1 = L.yard.y + L.yard.h;
  let fx0 = x0,
    fy0 = y0,
    fx1 = x1,
    fy1 = y1;
  const grow = (b: Bounds, ownLand?: string) => {
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w);
    y1 = Math.max(y1, b.y + b.h);
    if (ownLand) return;
    fx0 = Math.min(fx0, b.x);
    fy0 = Math.min(fy0, b.y);
    fx1 = Math.max(fx1, b.x + b.w);
    fy1 = Math.max(fy1, b.y + b.h);
  };
  const used = new Set<string>();
  const take = (tiles: string[]) => {
    for (const k of tiles) used.add(k);
    return tiles;
  };
  // The yard the house and barn stand on.
  parcels.push({
    tiles: take(slotTiles(L.yard, L.yard.w * L.yard.h)),
    t: 'yard',
    path: decor.path,
  });
  const tiers: Record<string, number> = {};
  for (const [id, ps] of Object.entries(state.year.producers)) {
    const s = L.slots[id];
    if (!s) continue;
    grow(s, s.land);
    // D268: the plot grows to its full size with every unit, then the milestones upgrade it.
    const { tier, sign } = tierOf(id, ps.owned, theme);
    tiers[id] = tier;
    if (s.house) {
      buildings.push({ ...s.house, style: styleOf(tier), ...look });
      take(slotTiles(s.house, s.house.w * s.house.h));
    }
    // D302: a plot with its own chain building holds back the tile reserved for it. D344: on a
    // kind's world, the exact tiles its buildings stand on (`hold`).
    const room = s.w * s.h - (s.hold ? s.hold.length : (s.chainReserve ?? 0));
    const rowTiles = (n: number) => (s.hold ? fillOrder(s, false).slice(0, n) : slotTiles(s, n));
    const fieldTiles = (n: number) => (s.hold ? fillOrder(s, true).slice(0, n) : blockTiles(s, n));
    // D345: no hand, and the one bar it fills sits full (barAt in view.ts, the same formula).
    // D354: or no hand and idle, waiting for its first tap (the row's own `idle`).
    const barReady =
      !ps.hand &&
      ps.owned > 0 &&
      (!ps.running || t - (state.advancedTo - ps.progressMs) >= ps.cycleMs);
    const extras = (p: IsoParcel): IsoParcel => {
      p.tier = tier;
      p.id = id;
      // D455: a marker (the ready badge) stands only where the player must act on the map: a
      // producer with no hand and a full bar. Buys, hires and chains show in the list, not here.
      // The marker wins over the level sign on the same building.
      const marker = barReady;
      if (sign && !marker) p.sign = sign;
      if (ps.hand) p.hand = true;
      if (opts.burst === id) p.burst = true;
      if (marker) p.ready = true;
      p.due = barReady;
      kit.dress?.(p, { id, owned: ps.owned, tier });
      return p;
    };
    if (s.animal) {
      parcels.push(
        extras({
          tiles: take(rowTiles(room).filter((k) => !used.has(k))),
          t: s.kind,
          // D329: a beast more for every milestone, as the pen fills in.
          n: Math.min(
            ps.owned,
            MAX_ANIMALS + Math.min(DENSITY.maxExtraAnimals, DENSITY.animalsPerTier * tier),
          ),
          animal: s.animal,
          fence: decor.fence,
          ...(s.mud ? { mud: true } : {}),
        }),
      );
      continue;
    }
    if (s.hives) {
      parcels.push(
        extras({
          tiles: take(rowTiles(room)),
          t: s.kind,
          hives: Math.min(MAX_HIVES, ps.owned),
        }),
      );
      continue;
    }
    if (s.kind === 'field' || s.crocus) {
      const n =
        ps.owned > 0 ? Math.min(room, (kit.layout?.firstPlot ?? FIRST_PLOT) + ps.owned - 1) : 0;
      const p: IsoParcel = extras({ tiles: take(fieldTiles(n)), t: s.kind });
      if (s.crocus) {
        p.crocus = true;
        parcels.push(p);
        continue;
      }
      // D353: a field a hand works runs its own cycle on the render clock (`atCycle`), never
      // the store's: here it is only marked, and drawn at the cycle's still point, so nothing
      // about it changes from one tick, tap or rebuild to the next. A field with no hand shows
      // its own bar.
      if (ps.hand) {
        p.cycle = true;
        p.stage = fieldCycleStage(kit.fieldCycle.still, kit.fieldCycle);
      } else p.stage = fieldStage(ps, state.advancedTo, t);
      if (s.crop) p.crop = s.crop;
      parcels.push(p);
      continue;
    }
    const p: IsoParcel = extras({
      tiles: take(rowTiles(Math.min(room, ps.owned))),
      t: s.kind,
    });
    if (s.vines) p.vines = true;
    if (s.hops) p.hops = true;
    if (s.glasshouse) p.glasshouse = true;
    parcels.push(p);
  }
  for (const id of state.year.chains ?? []) {
    const b = L.chains[id];
    if (!b) continue;
    grow(b);
    // D268: a chain's building steps up with the producer it lifts.
    const of = content.chains.find((c) => c.id === id)?.producer;
    // D340: once built, a chain's own row in the list is gone; a tap on its building finds
    // the producer's row instead, since that is where the chain's bonus shows. D343: `chain`
    // keeps the chain's own id too, for its own roof (`CHAIN_ROOF`).
    buildings.push(
      of && tiers[of] !== undefined
        ? { ...b, style: styleOf(tiers[of]), ...look, id: of, chain: id }
        : { ...b, ...look, ...(of ? { id: of } : {}), chain: id },
    );
    take(slotTiles(b, b.w * b.h));
  }
  // D245, D259: every festival leaves a mark beside the barn, one per year.
  // D327: once every kind of mark stands, later festivals gather on monument grounds instead,
  // each ground two mark plots side by side holding up to `MEMORIAL.perGround` memorials.
  const kinds = content.festival.marks.length;
  const grounds: IsoParcel[] = [];
  state.permanent.festivals.forEach((f, i) => {
    if (i >= kinds && kinds > 0) {
      const g = Math.floor((i - kinds) / MEMORIAL.perGround);
      let p = grounds[g];
      if (!p) {
        const a = L.markAt(kinds + 2 * g),
          b = L.markAt(kinds + 2 * g + 1);
        const at: Bounds =
          a.y === b.y && Math.abs(a.x - b.x) === a.w
            ? { x: Math.min(a.x, b.x), y: a.y, w: a.w + b.w, h: a.h }
            : a;
        grow(at);
        p = { tiles: take(slotTiles(at, at.w * at.h)), t: 'meadow', ground: { n: g, marks: [] } };
        grounds[g] = p;
        parcels.push(p);
      }
      p.ground!.marks.push(f.mark);
      if (opts.pulseMark === i) p.markNew = true;
      return;
    }
    const at = L.markAt(i);
    grow(at);
    const p: IsoParcel = { tiles: take(slotTiles(at, at.w * at.h)), t: 'meadow', mark: f.mark };
    if (opts.pulseMark === i) p.markNew = true;
    parcels.push(p);
  });
  // D301: a shut piece of land is never brought into camera; only a hint of it shows, a
  // strip of rough ground just past the hedge, and only once the world has grown to that
  // edge already (never widening the frame to find it).
  for (const land of Object.values(L.lands)) {
    if (land.open) continue;
    const b = land.bounds;
    const depth = 2;
    const strip: Bounds =
      b.x + b.w <= fx0
        ? { x: b.x + b.w - depth, y: b.y, w: depth, h: b.h }
        : b.x >= fx1
          ? { x: b.x, y: b.y, w: depth, h: b.h }
          : { x: b.x, y: b.y, w: b.w, h: depth };
    const hint = slotTiles(strip, strip.w * strip.h).filter((k) => {
      const [tx, ty] = k.split(',').map(Number) as [number, number];
      return tx >= fx0 && tx < fx1 && ty >= fy0 && ty < fy1 && !used.has(k);
    });
    if (hint.length) parcels.push({ tiles: take(hint), t: 'waste' });
  }
  const plot: Bounds = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  const worldPlot: WorldBounds = { x: fx0, y: fy0, w: fx1 - fx0, h: fy1 - fy0 };
  // D344: ground cut from a kind's outline is outside the fence: no slab, no grass, no hedge
  // across it. The renderers draw the world's edge round it (`worldOutline`).
  const cuts = L.cuts.flatMap((c) => {
    const x = Math.max(c.x, fx0),
      y = Math.max(c.y, fy0),
      w = Math.min(c.x + c.w, fx1) - x,
      h = Math.min(c.y + c.h, fy1) - y;
    return w > 0 && h > 0 ? [{ x, y, w, h }] : [];
  });
  if (cuts.length) worldPlot.cuts = cuts;
  const outside = new Set(cuts.flatMap((c) => slotTiles(c, c.w * c.h)));
  // D344: the pond shows once the world has grown round it.
  const pond = L.pond;
  if (pond && pond.x >= fx0 && pond.y >= fy0 && pond.x + pond.w <= fx1 && pond.y + pond.h <= fy1)
    parcels.push({ tiles: take(slotTiles(pond, pond.w * pond.h)), t: 'pond' });
  // Everything not yet put to use is grass, waiting for the next purchase. D301: the world and
  // an open piece of land beside it are grassed apart, with the river's gap between them left
  // bare, so the two read as separate fenced land, not one field run together.
  const grassOf = (b: Bounds) =>
    slotTiles(b, b.w * b.h).filter((k) => !used.has(k) && !outside.has(k));
  const grass = grassOf(worldPlot);
  if (grass.length) parcels.unshift({ tiles: grass, t: 'meadow' });
  const openLands = Object.values(L.lands)
    .filter((l) => l.open)
    .map((l) => l.bounds);
  for (const b of openLands) {
    const g = grassOf(b);
    if (g.length) parcels.push({ tiles: g, t: 'meadow' });
  }
  // D267: the land round the world. Trees on land in use would stand in a plot, so not those.
  const country: CountryItem[] = L.trees
    .filter((q) => !used.has(`${q.x},${q.y}`))
    .map((q) => ({ x: q.x, y: q.y, w: 1, h: 1, t: 'cwood' }));
  // D287: the stream became the river, and both renderers draw it where the stream ran.
  // D348: a theme with no edge water draws none.
  const stream = (kit.edgeWater ? L.river : []).map(
    (q) => [q.x + 0.5, q.y + 0.5] as [number, number],
  );
  // D329: the neighbours' fields, hedges, copses, the lane and the village beyond.
  country.push(
    ...kit.landscape(
      L,
      plot,
      Object.values(L.lands).map((l) => l.bounds),
    ),
  );
  const workerDef = content.decorations.find((d) => d.kind === 'worker')?.id;
  const track = idleTrackFor(state, content);
  const paving = lanePaving(state, opts.theme, L);
  const snow = worldSnow(state, t, opts.theme);
  const fillers = worldFillers(L);
  const gateLast = L.lane[L.lane.length - 1],
    gatePrev = L.lane[L.lane.length - 2];
  return {
    decor: {
      hedge: decor.hedge,
      gate: decor.gate,
      ...(fenceOnBoundary(kit) ? { fence: decor.fence } : {}),
      ...(gateLast && gatePrev
        ? { gateAt: { x: gateLast.x, y: gateLast.y, px: gatePrev.x, py: gatePrev.y } }
        : {}),
      ...(track.edges.length ? { track: { surface: track.surface, edges: track.edges } } : {}),
      ...(paving.length ? { paving } : {}),
      ...(snow > 0 ? { snow } : {}),
      ...(fillers.length ? { fillers } : {}),
    },
    // D331: the hands' clothes ride on every parcel, as the fence does.
    parcels:
      decor.worker === workerDef ? parcels : parcels.map((p) => ({ ...p, workers: decor.worker })),
    buildings,
    plot,
    worldPlot,
    lands: openLands,
    country,
    stream,
    theme: kit.id,
  };
}

/**
 * D437: the heart's form for a world: its producers running and its festivals held, through
 * the one shared clock (`heartForm`).
 */
export function worldForm(state: IdleWorldState): 0 | 1 | 2 {
  const running = Object.values(state.year.producers).filter((p) => p.owned > 0).length;
  return heartForm(running, state.permanent.festivals.length);
}

/**
 * D437: from the heart's form 1, the one-tile gaps between neighbouring plots, paved in the
 * theme's paving by both maps: never the river, the pond, the yard or a festival's mark.
 */
export function lanePaving(
  state: IdleWorldState,
  stored?: string,
  L: WorldLayout = worldLayout(state, stored),
): TileKey[] {
  if (worldForm(state) < 1) return [];
  const plots: Bounds[] = [];
  for (const [id, ps] of Object.entries(state.year.producers)) {
    const s = L.slots[id];
    if (s && !s.land && ps.owned > 0) plots.push({ x: s.x, y: s.y, w: s.w, h: s.h });
  }
  const keep = new Set<TileKey>([
    ...L.river.map((q) => `${q.x},${q.y}`),
    ...slotTiles(L.yard, L.yard.w * L.yard.h),
    ...(L.pond ? slotTiles(L.pond, L.pond.w * L.pond.h) : []),
  ]);
  state.permanent.festivals.forEach((_, i) => {
    const m = L.markAt(i);
    for (const k of slotTiles(m, m.w * m.h)) keep.add(k);
  });
  return plotGaps(plots, keep);
}

/** D440: how far winter's snow lies on the world at `t`, 0 to 1 (`snowAmount`). */
export function worldSnow(state: IdleWorldState, t: number, stored?: string): number {
  const s = idleSeason(state, getIdleContent(themeOf(state, stored)), t);
  return snowAmount(s.season, s.seasonProgress);
}

/** D440: a filler on a lone tile: the tile, and which of the theme's four (0 to 3). */
export interface Filler {
  key: TileKey;
  x: number;
  y: number;
  variant: number;
}

/** D440: the variant for a lone tile, from the world's seed and the tile, so neighbours differ. */
export function fillerVariant(seed: number, key: TileKey): number {
  let h = 2166136261 ^ (seed >>> 0);
  for (const ch of `${key}`) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return ((h >>> 0) ^ ((h >>> 0) >>> 13)) % 4;
}

/** D440: a filler for each lone tile the seed left. */
export function worldFillers(L: WorldLayout): Filler[] {
  return L.lone.map((key) => {
    const [x, y] = key.split(',').map(Number) as [number, number];
    return { key, x, y, variant: fillerVariant(L.seed, key) };
  });
}

/* ---------- D329: the country beyond the fence ---------- */

/**
 * One thing in the country round the world, as both renderers take it. `t` says what:
 * `cwood` a seeded tree in the margin (D267), and from D329 `cfield` a neighbour's field (`k`
 * its crop from `LANDSCAPE_CROP`, `r` 1 when its rows run along y), `chedge` a hedge along a
 * line (`w` or `h` is 0), `chtree` a hedgerow or copse tree (`s` its size), `clane` the lane
 * or the village street, `chouse` a village house or the church (`k`), `cbeast` a sheep or a
 * cow grazing (`k`, `s` the way it faces). D344: `chills` is no thing at all but the world
 * kind's scale on the 3D country's hills (`s`), at the country's far corner.
 */
export interface CountryItem {
  x: number;
  y: number;
  w: number;
  h: number;
  t: string;
  k?: string;
  r?: number;
  s?: number;
}
